import { memo, useCallback } from 'react';
import { useFocusEffect } from '@bonanza-social/navigation';
import { Trans, useTranslation } from '@bonanza-social/i18n';
import {
  useDispatch,
  useSelector,
  selectTokenName,
} from '@bonanza-social/toolkit/state';
import { Box, ScrollView, Text } from '@bonanza-social/moonlight';
import { formatDate } from '@bonanza-social/toolkit/utils';
import {
  selectPrizeWallWinnersNextWallStartDate,
  selectPrizeWallWinnersByRewardId,
  selectPrizeWallWinnersDataState,
  fetchPrizeWallWinners,
  selectPrizeWallWinners,
} from '../../state/prizeWallWinners/prizeWallWinners.slice';
import { selectPrizeWallPrizes } from '../../state/prizeWall/prizeWall.slice';
import { PrizeWallWinnersProps } from './PrizeWallWinners.types';
import PrizeWallWinnersSkeleton from './PrizeWallWinnersSkeleton/PrizeWallWinnersSkeleton';

function PrizeWallWinners(props: PrizeWallWinnersProps) {
  const dispatch = useDispatch();
  const [t] = useTranslation('prizeWall');
  const tokenName = useSelector(selectTokenName);
  const winners = useSelector(selectPrizeWallWinners);
  const winnersByReward = useSelector(selectPrizeWallWinnersByRewardId);
  const prizes = useSelector(selectPrizeWallPrizes);
  const dataState = useSelector(selectPrizeWallWinnersDataState);
  const nextWallDate = useSelector(selectPrizeWallWinnersNextWallStartDate);

  useFocusEffect(
    useCallback(() => {
      dispatch(fetchPrizeWallWinners());
    }, [dispatch]),
  );

  if (
    dataState === 'pending' ||
    !winners?.length ||
    !winnersByReward ||
    !prizes
  ) {
    return <PrizeWallWinnersSkeleton {...props} />;
  }

  return (
    <Box flex={1} {...props}>
      <Box flex={1} gap="space-200" paddingHorizontal="space-400">
        <Text
          variant="body-m-semi"
          textAlign="center"
          color="darkSurfacePrimary"
        >
          {t('prizeWallWinnersTitle', {
            context: nextWallDate ? 'pending' : 'over',
          })}
        </Text>
        {nextWallDate && (
          <Text variant="caption" textAlign="center" color="darkSurfacePrimary">
            <Trans
              ns="prizeWall"
              i18nKey="prizeWallWinnersSubtitle"
              values={{
                day: formatDate(nextWallDate, 'dddd'),
                hour: formatDate(nextWallDate, 'HH:mm'),
                tokenName,
              }}
              components={{
                time: <Text variant="caption-semi" />,
              }}
            />
          </Text>
        )}
        <Box
          flex={1}
          marginTop="space-400"
          borderRadius="radius-300"
          backgroundColor="primaryBackgroundHover"
        >
          <ScrollView
            contentContainerStyle={{
              gap: 'space-600',
              padding: 'space-400',
              paddingBottom: 'space-1000',
            }}
          >
            {winnersByReward &&
              Object.keys(winnersByReward).map((rewardId) => (
                <Box key={rewardId}>
                  <Text variant="body-m" gradient="darkSurfaceAccent">
                    {prizes.byId[rewardId]?.name}
                  </Text>
                  <Box
                    marginTop="space-200"
                    borderTopWidth="border-width-025"
                    borderColor="borderSecondary"
                  />
                  {winnersByReward[rewardId].map((winner, index) => (
                    <Text
                      key={index} // Username can be duplicated
                      variant="caption"
                      color="darkSurfacePrimary"
                      paddingTop="space-400"
                    >
                      {winner.username}
                    </Text>
                  ))}
                </Box>
              ))}
          </ScrollView>
        </Box>
      </Box>
    </Box>
  );
}

export default memo(PrizeWallWinners);
